import { useState, useEffect } from 'react'
import { useAuthFetch } from '../lib/useAuthFetch'

const API = import.meta.env.VITE_API_URL ?? ''

// "you vs the nearby market" verdict for a brand (your avg price vs rival avg).
// 'no_own_price' = you don't carry this brand (or it hasn't been collected).
export type BrandVerdict = 'higher' | 'cheaper' | 'parity' | 'no_own_price'

export interface BrandCarrier {
  competitor_id: string
  name: string
  distance_mi: number | null
  price: number
  on_sale: boolean
  item_count: number
}

export interface BrandRow {
  brand: string            // display label (real menu_items.brand — never faked)
  brand_key: string        // normalized identity key
  carrier_count: number    // N — nearby shops carrying it
  ubiquity: number         // N / M, 0..1
  avg_price: number
  min_price: number
  max_price: number
  any_on_sale: boolean
  total_items: number
  your_price: number | null
  your_on_sale: boolean | null
  verdict: BrandVerdict
  carriers: BrandCarrier[] // sorted cheapest-first by the API
}

export interface RivalRef {
  competitor_id: string
  name: string
  business_type: string | null
  distance_mi: number | null
}

export interface BrandAreaData {
  location: { id: string; name: string }
  radius_mi: number
  category: string | null
  categories: string[]     // dropdown vocabulary — only categories with data in range
  total_rivals: number     // M — physical shops in range with menu data
  rivals: RivalRef[]
  brands: BrandRow[]
}

export function useBrandArea(
  locationId: string | null,
  radius: number,
  category?: string,
  type?: string
) {
  const authFetch = useAuthFetch()
  const [data, setData] = useState<BrandAreaData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!locationId) return
    let alive = true
    setLoading(true)
    setError(null)
    const params = new URLSearchParams({ location_id: locationId, radius: String(radius) })
    if (category) params.set('category', category)
    if (type && type !== 'both') params.set('type', type)

    authFetch(`${API}/api/v1/brands/area?${params}`)
      .then(async (r) => {
        const body = await r.json()
        if (!r.ok || body?.success === false) {
          throw new Error(body?.error || 'Couldn’t load brand data.')
        }
        return body
      })
      .then((body) => {
        if (!alive) return
        setData(body.data as BrandAreaData)
        setLoading(false)
      })
      .catch((err) => {
        if (!alive) return
        setError(err.message)
        setData(null)
        setLoading(false)
      })
    return () => { alive = false }
  }, [locationId, radius, category, type, authFetch])

  return { data, loading, error }
}
